import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  WORD_STATISTICS_VALUE,
  VOCA_TYPE_STYLE,
  MAX_VOCA_COUNT,
} from '../../data/common';
import { styled } from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { strParse } from '../../lib/util';
import CheckBox from '../common/CheckBox';
import CheckIc from '../../assets/images/ic_check_black.svg';
import UnCheckIc from '../../assets/images/ic_uncheck.svg';
import AlertModal from '../common/AlertModal';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

const wordStatisticsStyle = [
  {
    id: WORD_STATISTICS_VALUE.Entry,
    color: '#10BDA9',
    bgColor: '#E2FAF7',
  },
  {
    id: WORD_STATISTICS_VALUE.Derived,
    color: '#F636C9',
    bgColor: '#FCE0F6',
  },
  {
    id: WORD_STATISTICS_VALUE.Reference,
    color: '#3D57BD',
    bgColor: '#E0EAFF',
  },
  {
    id: WORD_STATISTICS_VALUE.Related,
    color: '#F59E0B',
    bgColor: '#FEF3C7',
  },
];

function VocaRow({
  voca,
  vocabularyList,
  setVocabularyList,
  onAddSelectVoca,
  checkedLength,
}) {
  const [countAlert, setCountAlert] = useState(null);
  const [idForTypeListOpen, setIdForTypeListOpen] = useState(null);
  const wordStatisticStyle = wordStatisticsStyle.find(
    style => style.id === voca.vc_type,
  );

  // 타입클릭시 타입결정 영역 열기
  const onTypeClick = vocaIdx => {
    if (idForTypeListOpen === vocaIdx) {
      setIdForTypeListOpen(null);
    } else {
      setIdForTypeListOpen(vocaIdx);
    }
  };

  // 단어의 타입결정
  const onDecideType = selectedType => {
    if (voca.checked) {
      setVocabularyList(
        vocabularyList.map(item =>
          item.idx === voca.idx ? { ...item, chosenType: selectedType } : item,
        ),
      );
    } else {
      if (checkedLength === MAX_VOCA_COUNT) {
        setCountAlert(`문제 출제는 최대 ${MAX_VOCA_COUNT}개까지 가능합니다.`);
        return;
      }
      setVocabularyList(
        vocabularyList.map(item =>
          item.idx === voca.idx
            ? { ...item, chosenType: selectedType, checked: true }
            : item,
        ),
      );
    }
    setIdForTypeListOpen(null);
  };

  const tooltipMeaning = () => {
    return voca.meanings
      ?.map(meaning => strParse(meaning?.mi_meaning)) // 배열 변환
      .join('; ');
  };

  return (
    <div
      className="w-auto flex flex-col justify-start whitespace-nowrap items-start border-b border-[#E7E7E7] min-h-[47px] px-[8px]"
      style={{
        width: 'auto',
        whiteSpace: 'nowrap',
        background: idForTypeListOpen === voca.idx ? '#f1f5f9' : '#FFF',
        minHeight: idForTypeListOpen === voca.idx ? '80px' : '40px',
      }}
    >
      <div className="w-auto min-h-[40px] flex justify-start items-center gap-2">
        <CheckBox
          id={voca.idx}
          value={voca.idx}
          isChecked={voca.checked}
          onChange={onAddSelectVoca}
          checkedIcon={CheckIc}
          uncheckedIcon={UnCheckIc}
          boxStyle={{ width: '24px', minWidth: '24px', height: '24px' }}
        />
        <button
          type="button"
          className="flex justify-center items-center w-[24px] min-w-[24px] h-[24px] rounded-xl bg-slate-300 text-[14px] font-[700]"
          onClick={() => onTypeClick(voca.idx)}
          style={
            voca.chosenType !== null
              ? VOCA_TYPE_STYLE.SELECTED
              : VOCA_TYPE_STYLE.NORMAL
          }
        >
          {voca?.chosenType ?? 'A'}
        </button>
        <div
          className="w-[20px] h-[20px] text-[12px] font-[500] text-[#00B19D] flex justify-center items-center whitespace-nowrap rounded min-h-[20px]"
          style={{
            color: wordStatisticStyle.color,
            background: wordStatisticStyle.bgColor,
          }}
        >
          {voca.vc_type === WORD_STATISTICS_VALUE.Entry && '표'}
          {voca.vc_type === WORD_STATISTICS_VALUE.Derived && '파'}
          {voca.vc_type === WORD_STATISTICS_VALUE.Reference && '참'}
          {voca.vc_type === WORD_STATISTICS_VALUE.Related && '관'}
        </div>
        <HtmlTooltip
          title={
            <React.Fragment>
              <span style={{ fontSize: '16px', fontFamily: 'Pretendard' }}>
                {voca.word}
              </span>
            </React.Fragment>
          }
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [-1.5, -12],
                  },
                },
              ],
            },
          }}
          placement="top-start"
        >
          <div className="w-[100px] h-[18px] min-w-[100px] text-[16px] font-[700] overflow-hidden text-ellipsis text-[#1E293B] leading-[16px] ">
            {voca.word}
          </div>
        </HtmlTooltip>
        <div
          className="w-[60px] min-w-[60px] font-[400] text-[12px] text-[#969BA4] overflow-hidden text-ellipsis"
          style={{ textAlign: 'center' }}
        >
          {voca.unit_short}
        </div>
        <div className="w-[113px] min-w-[113px] overflow-hidden text-ellipsis whitespace-nowrap self-stretch my-auto text-sm leading-none text-slate-800">
          <HtmlTooltip
            title={
              <React.Fragment>
                <span style={{ fontSize: '14px', fontFamily: 'Pretendard' }}>
                  {tooltipMeaning()}
                </span>
              </React.Fragment>
            }
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [-1.5, -12],
                    },
                  },
                ],
              },
            }}
            placement="top-start"
          >
            <span className="overflow-hidden text-ellipsis">
              {tooltipMeaning()}
            </span>
          </HtmlTooltip>
          {/* {voca.meanings?.map((meaning, index) => {
            return (
              <HtmlTooltip
                key={`${meaning.idx}-${String(index)}`}
                title={
                  <React.Fragment>
                    <span
                      style={{ fontSize: '14px', fontFamily: 'Pretendard' }}
                    >
                      {tooltipMeaning()}
                    </span>
                  </React.Fragment>
                }
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [-1.5, -12],
                        },
                      },
                    ],
                  },
                }}
                placement="top-start"
              >
                <span
                  key={`${meaning.idx}-${String(index)}`}
                  className="overflow-hidden text-ellipsis"
                >
                  {tooltipMeaning()}
                </span>
              </HtmlTooltip>
            );
          })} */}
        </div>
      </div>
      <div
        className="flex items-center justify-center w-full overflow-hidden transition-all "
        style={{ height: idForTypeListOpen === voca.idx ? '40px' : '0' }}
      >
        <div className="flex gap-[5px] justify-center items-center ">
          {voca.problemTypes
            ?.sort((a, b) => a.type.localeCompare(b.type))
            ?.map((pType, index) => {
              return (
                <button
                  key={`${pType.idx}-${String(index)}`}
                  type="button"
                  className="flex justify-center items-center w-[24px] h-[24px] rounded-xl bg-slate-300 text-[14px] font-[700]"
                  onClick={() => onDecideType(pType.type)}
                  style={
                    pType.type === voca.chosenType
                      ? VOCA_TYPE_STYLE.SELECTED
                      : {
                          ...VOCA_TYPE_STYLE.DISSELECTED,
                          boxShadow: '0px 0px 3px 0px rgba(0, 0, 0, 0.25)',
                        }
                  }
                >
                  {pType.type}
                </button>
              );
            })}
        </div>
      </div>
      <AlertModal
        open={countAlert !== null ? true : false}
        setOpen={setCountAlert}
        content={countAlert}
        btnText="확인"
        onConfirm={() => setCountAlert(null)}
      />
    </div>
  );
}

VocaRow.propTypes = {
  voca: PropTypes.object.isRequired,
  vocabularyList: PropTypes.array.isRequired,
  setVocabularyList: PropTypes.func.isRequired,
  onAddSelectVoca: PropTypes.func.isRequired,
  checkedLength: PropTypes.number.isRequired,
};

export default React.memo(VocaRow);
